<template>
  <div class="timeline" :class="{ isBreak }">
    <div
      class="line before"
      :style="{
        height: beforeHeight,
      }"
    >
      <div v-if="displayCurrentTime && !hideTime" class="highlight" />
    </div>
    <div
      v-if="displayTime"
      class="time"
      :class="{ current: current && displayCurrentTime }"
    >
      <span>
        {{ time }}
      </span>
      <span v-if="ampm" class="ampm">{{ ampm }}</span>
    </div>
    <div
      class="line"
      :style="{
        height: afterHeight,
      }"
    >
      <div v-if="displayCurrentTime && !hideTime" class="highlight" />
    </div>
  </div>
</template>

<script lang="ts">
import { format } from "date-fns";
import { computed, ComputedRef, defineComponent, PropType, toRefs } from "vue";

import { EventOrPause } from "@ctrl/controller-rpc";

import { useDndEvent } from "@/components/agenda/dnd";
import {
  useEventProgress,
  useHeightOfDuration,
} from "@/components/agenda/pixels";
import { getDaytime } from "@/components/dates";
import * as inject from "@/components/inject";
import { UseSettings } from "@/components/settings/controller/settings";

export default defineComponent({
  props: {
    event: { type: Object as PropType<EventOrPause>, required: true },
    hideTime: {
      type: Boolean,
      default: false,
    },
    isBreak: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const clock = inject.clock();
    const locale = inject.locale();
    const { event, isBreak, hideTime } = toRefs(props);
    const { ongoing } = useHeightOfDuration(event, clock);
    const { progress } = useEventProgress(event, clock);
    const dndEvent = computed(useDndEvent);
    const current = computed(() => ongoing.value);
    const displayCurrentTime: ComputedRef<boolean> = computed(
      () => current.value && !dndEvent.value && isBreak.value
    );
    const displayTime = computed(
      () => !hideTime.value && (!isBreak.value || displayCurrentTime.value)
    );
    const compact = computed(
      () => (+event.value.end - +event.value.start) / 1000 / 60 <= 15
    );

    //All this should go away when the controller handle those formats
    const { is24hFormat } = UseSettings.getInstance();
    const formatAMPMTime = (date: Date) => format(date, "h:mm");
    const formatAMPM = (date: Date) => format(date, "a");
    const ampm: ComputedRef<string | null> = computed(() =>
      !is24hFormat.value && (!current.value || !isBreak.value)
        ? formatAMPM(event.value.start)
        : null
    );
    const formatTime = (date: Date) =>
      is24hFormat.value
        ? locale.value.formatDaytime(getDaytime(date))
        : formatAMPMTime(date);
    const time: ComputedRef<string> = computed(() =>
      current.value && isBreak.value
        ? formatTime(clock.value)
        : formatTime(event.value.start)
    );

    const beforeHeight: ComputedRef<string> = computed(() =>
      isBreak.value
        ? progress.value > 100
          ? "100%"
          : `${progress.value}%`
        : compact.value
          ? "11px"
          : "2px"
    );
    const afterHeight: ComputedRef<string> = computed(() =>
      isBreak.value
        ? progress.value > 100
          ? "0%"
          : `${100 - progress.value}%`
        : "100%"
    );
    return {
      afterHeight,
      ampm,
      beforeHeight,
      clock,
      current,
      displayCurrentTime,
      displayTime,
      getDaytime,
      locale,
      progress,
      time,
    };
  },
  computed: {},
});
</script>

<style lang="scss" scoped>
.timeline {
  z-index: $z-index-content;
  height: 100%;
  min-height: 18px;
  width: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 0;

  .time {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 2px;
    padding: 3px 6px;
    margin-top: 8px;
    border-radius: 30px;
    z-index: $z-index-content-elevated;
    @include themed() {
      color: t($grey-40);
      background: t($bg);
    }

    &.current {
      font-weight: 600;
      @include themed() {
        color: t($secondary);
      }
    }
    .ampm {
      font-size: 8px;
    }
  }

  &.isBreak {
    .time.current {
      @include themed() {
        background-color: t($secondary-10);
      }
    }
  }

  .line {
    height: 100%;
    width: 0.5px;
    position: relative;
    @include themed() {
      background: t($grey-15);
    }
    .highlight {
      height: 32px;
      position: absolute;
      z-index: $z-index-content;
      width: 0.5px;
      @include themed() {
        border-image: linear-gradient(to top, t($secondary), rgba(0, 0, 0, 0)) 1
          100%;
        background: linear-gradient(
          180deg,
          t($secondary) 0%,
          rgba(0, 0, 0, 0) 100%
        );
      }
    }
    &.before {
      .highlight {
        bottom: 0;
        @include themed() {
          border-image: linear-gradient(to top, t($secondary), rgba(0, 0, 0, 0))
            1 100%;
          background: linear-gradient(
            0deg,
            t($secondary) 0%,
            rgba(0, 0, 0, 0) 100%
          );
        }
      }
    }
  }
}
</style>
